import { FC } from 'react';

import type { INewWalletQuery, IWallet, TWalletType } from '@entities/wallets';
import { useCreateWalletMutation } from '@entities/wallets';
import { ModalNewEntity } from '@features/modal-entity-new';

import { useWalletCreate } from './useWalletCreate';

interface IProps {
  wallet: IWallet;
  refetch: () => void;
}

export const getClonedName = (name: string): string => {
  const match = name.match(/^(.*?)\s*\((\d+)\)$/);
  if (match) {
    return `${match[1]} (${parseInt(match[2], 10) + 1})`;
  }
  return `${name} (1)`;
};

export const CloneWallet: FC<IProps> = ({ wallet, refetch }) => {
  const { handleCreateWallet, handleSuccess } = useWalletCreate(refetch);

  const baseWalletKeys: (keyof INewWalletQuery)[] = [
    'type',
    'provider_id',
    'bank_id',
    'name',
    'wallet_address',
    'bank_code',
    'holder_name',
    'bank_account_number',
  ];

  const getCloneKeys = (): (keyof INewWalletQuery)[] => {
    switch (wallet.type as TWalletType) {
      case 'settlement':
        return ['merchant_id', 'mid_id', 'provider_id', 'type'];
      case 'withdraw':
        return [...baseWalletKeys, 'login_credentials_json'];
      case 'deposit':
        return [...baseWalletKeys, 'kind'];
      default:
        return baseWalletKeys;
    }
  };

  const defaultValues: Record<keyof Omit<INewWalletQuery, 'wallet_address' | 'type'>, string> = {
    provider_id: wallet.provider.id,
    bank_id: wallet.bank.id,
    name: getClonedName(wallet.name),
    bank_code: wallet.bank_code,
    holder_name: wallet.wallet_holder_name,
    bank_account_number: wallet.bank_account_number,
    kind: wallet.kind ?? '',
    login_credentials_json: wallet.login_credentials_json,
    merchant_id: wallet.merchant_id,
    mid_id: wallet.mid.id,
  };

  const renamedLabels = {
    bank_id: 'Bank',
    provider_id: 'Provider',
    mid_id: 'MID',
    merchant_id: 'Merchant',
    bank_account_number: 'Bank Account Number',
  };

  return (
    <ModalNewEntity
      title='Clone Wallet'
      submitBtnText='Clone'
      entityKeys={getCloneKeys()}
      renamedLabels={renamedLabels}
      useCreateEntityMutation={useCreateWalletMutation}
      onTransformFormData={handleCreateWallet}
      onSuccess={handleSuccess}
      otherProps={{ type: wallet.type }}
      defaultValues={defaultValues}
      autoFocusField='wallet_address'
      keysOrder={
        wallet.type === 'withdraw' ? [...baseWalletKeys, 'login_credentials_json'] : baseWalletKeys
      }
    />
  );
};
